import React, { useEffect, useState } from 'react';
import { Lightbulb, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAuth } from '../../contexts/AuthContext';

const SESSION_KEY_PREFIX = 'odontoassist:onboarding-tip-shown';

const ContextualOnboardingTip = () => {
  const onboarding = useOnboarding();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const onboardingState = onboarding?.state;
  const recordInteraction = onboarding?.interaction;
  const tip = onboardingState?.enabled ? onboardingState.contextual_tip : null;
  const sessionKey = `${SESSION_KEY_PREFIX}:${user?.id || user?.email || 'anonymous'}:${tip?.id || 'none'}`;
  const tipPath = tip?.route?.split('?')[0];
  const isRelevantRoute = Boolean(
    tipPath && (
      location.pathname === tipPath ||
      location.pathname.startsWith(`${tipPath}/`)
    )
  );

  useEffect(() => {
    setVisible(false);
    if (!tip || !isRelevantRoute || sessionStorage.getItem(sessionKey)) return undefined;
    const timer = window.setTimeout(() => {
      sessionStorage.setItem(sessionKey, 'true');
      setVisible(true);
      recordInteraction?.('tip_shown', tip.id);
    }, 1800);
    return () => window.clearTimeout(timer);
  }, [tip, isRelevantRoute, sessionKey, recordInteraction]);

  if (!tip || !visible) return null;

  const showTour = async () => {
    await onboarding.interaction('tour_started', tip.id);
    setVisible(false);
    navigate(tip.route);
    window.setTimeout(() => window.dispatchEvent(new CustomEvent('odontoassist:start-guided-tour', { detail: { stepId: tip.id } })), 250);
  };

  const snooze = async () => {
    setVisible(false);
    await onboarding.interaction('snoozed', tip.id);
    await onboarding.refresh();
  };

  const dismiss = async () => {
    setVisible(false);
    await onboarding.interaction('dismissed', tip.id);
    await onboarding.refresh();
  };

  return <aside className="fixed bottom-40 right-6 z-40 max-h-[calc(100vh-11rem)] max-w-sm overflow-y-auto rounded-xl border border-indigo-200 bg-white p-4 shadow-xl" style={{ width: 'calc(100% - 3rem)' }} aria-label="Dica do OdontoAssist AI">
    <button type="button" onClick={snooze} className="absolute right-2 top-2 rounded p-1 text-gray-400 hover:bg-gray-100" aria-label="Fechar e lembrar depois"><X className="h-4 w-4" /></button>
    <div className="flex gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700"><Lightbulb className="h-5 w-5" /></span>
      <div className="min-w-0 pr-4">
        <p className="font-semibold text-gray-900">{tip.title}</p>
        <p className="mt-1 text-sm leading-5 text-gray-600">{tip.description}</p>
        {tip.missing_requirements?.length > 0 && (
          <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p className="font-semibold">Ainda falta:</p>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              {tip.missing_requirements.map((requirement) => (
                <li key={requirement.id}>{requirement.title}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 pl-12 text-sm font-semibold">
      <button type="button" onClick={showTour} className="text-indigo-700 hover:text-indigo-900">Ver como fazer</button>
      <button type="button" onClick={snooze} className="text-gray-600 hover:text-gray-900">Lembrar em 3 dias</button>
      <button type="button" onClick={dismiss} className="text-gray-500 hover:text-gray-800">Não mostrar dicas novamente</button>
    </div>
  </aside>;
};

export default ContextualOnboardingTip;
